<script lang="ts">
/** 빈 상태 값 — 세 단(inline · boxed · page)과 옛 별칭(row · center · band) */
export type EmptyStateVariant =
  "inline" | "boxed" | "page" | "band" | "center" | "row" | "roadmap";

/** DEV: 이미 낸 같은 제목 경고(같은 글은 한 번만) */
const warnedTitles = new Set<string>();
</script>

<script setup lang="ts">
import { computed, onMounted, onUpdated } from "vue";

import UiIcon from "@/components/UiIcon.vue";

/**
 * 빈 상태(`.ui-empty`). 모양은 전역 규칙을 그대로 받는다 — 카드 안은 글자만(7차 S2), 상자 · 띠 · 가운데는 변형 클래스.
 * - 제목은 `b.ui-empty-title`, 설명은 `p`, 이어지는 버튼은 `actions` 슬롯(`.actions`).
 * - `icon="check"` 는 "처리할 것 없음"(대시보드 리스크 0건), `info` 는 아직 데이터 없음, `sparkle` 은 AI 도우미 빈 대화(73차 UXM-02).
 * - `variant="row"` 는 `<tbody>` 안 한 행(`tr.ui-empty-row > td[colspan]`)이고 안쪽은 center 모양으로 자기 자신을 그린다.
 * - 80차 UXS80-08 · L10 **3단**: `inline`(한 줄 · 목록 안 · 카드 안 글자만) · `boxed`(카드 안 자리 — 아이콘 + 제목 + 한 줄 + 버튼 **하나**) ·
 *   `page`(화면 전체 · 넓은 영역 가운데). 옛 값은 별칭이라 호출부는 그대로다: `row` = `inline` 을 표 안 한 행(`<tr>`)으로, `center` · `band` = `page`.
 *   (`ghost` · `text` 는 이 부품의 값이 아니다 — 슬롯 안 버튼의 variant 다.) 루트에 `data-variant="inline | boxed | page"` 가 붙는다. 화면 전체 여백(위아래 24px)은 **명시적 `variant="page"` 만** 받는다(`is-page` 클래스) —
 *   옛 별칭 center · band 는 모양이 바뀌지 않게 `data-variant` 만 page 로 읽히고 `is-page` 는 받지 않는다.
 * - 81차 `variant="roadmap"`(UXB81-17 · b-C10): 「처음 오는 사람에게 앞으로의 단계를 보이는 빈 상태」 — boxed 모양 가운데 정렬 + 제목 + 설명 + **단계 줄**(`steps` 글 목록 → 번호 칩 사슬, 또는 기본 슬롯에 직접)
 *   + 주 버튼 하나(`#actions`). 한 줄 회색 글 + 버튼만 서 있어 이 화면이 어디로 이어지는지 알 수 없던 직무적합성 빈 화면용. `steps` 는 장식 순서일 뿐 누를 수 없다(`ol` — 낭독은 순서 목록).
 * - `icon` 을 주지 않으면(자동) `boxed` · `page` 는 `info`, `inline` 은 없음이다. 아이콘이 싫으면 `icon="none"`. 제목이 없으면 아이콘도 없다.
 * - 90차 C90-16(UXS90-03 ③) 머리 규칙: ① `page` 는 부모가 **카드 안**이면 상자 없이 가운데 글만(오른쪽 상세 · 카드 본문), **독립 화면**(카드 밖 — 채용 홈페이지)이면 r22 상자.
 *   ② **문장 하나만** 전하면 제목 없이 `description` 만(`inline`) — 문장 통째를 `title` 로 보내면 굵은 14/800 문단이 된다(`TargetList` `emptyText` 가 그랬다).
 *   제목은 짧은 이름(「아직 직무가 없습니다」)일 때만, 그 아래 설명 + 버튼 하나. 「직무가 없다」 는 경고(`Callout`)가 아니라 `boxed` — 막힘이 아니라 빈 것이다.
 * - 한 화면에 같은 이유의 빈 상태는 한 곳만 그린다(왼쪽 목록이 비면 오른쪽 상세는 `v-if` 로 조용히 숨김). 같은 제목이 한 화면에 둘이면 DEV 경고.
 */
const props = withDefaults(
  defineProps<{
    /** 제목 */
    title?: string;
    /** 설명 문장 */
    description?: string;
    /**
     * inline = 카드 안 글자, boxed = 상자(아이콘 + 제목 + 한 줄 + 버튼 하나), page = 화면 전체 가운데.
     * 별칭(호출부 무수정): row = 표 안 한 행(inline), center · band = page(band 는 페이지 최상위 띠 클래스를 유지)
     */
    variant?: EmptyStateVariant;
    /** row 일 때 칸이 차지할 열 수 */
    colspan?: number;
    /** 제목 앞 표지. 생략하면 자동(boxed · page = info, inline = 없음), `none` 은 늘 없음 */
    icon?: "none" | "check" | "info" | "sparkle";
    /** 바뀌면 낭독할 때 status */
    role?: "status";
    /** `variant="roadmap"` 의 단계 글(번호 칩 사슬). 가로 폭이 모자라면 줄바꿈 */
    steps?: string[];
  }>(),
  {
    title: undefined,
    description: undefined,
    variant: "inline",
    colspan: 1,
    icon: undefined,
    role: undefined,
    steps: () => [],
  },
);

defineSlots<{
  /** 제목 · 설명 아래 추가 내용 */
  default?: () => unknown;
  /** 이어지는 버튼 줄(.actions) */
  actions?: () => unknown;
}>();

/** 세 단 */
type Tier = "inline" | "boxed" | "page";

/** 옛 값 → 세 단 */
const TIER_OF: Record<NonNullable<typeof props.variant>, Tier> = {
  inline: "inline",
  boxed: "boxed",
  page: "page",
  band: "page",
  center: "page",
  row: "inline",
  roadmap: "boxed",
};

/** 전역 CSS 클래스(옛 값이 쓰던 그대로 — 모양이 바뀌지 않게 한다) */
const VARIANT_CLASSES: Record<NonNullable<typeof props.variant>, string> = {
  inline: "",
  boxed: "is-boxed",
  page: "is-center",
  band: "ui-band",
  center: "is-center",
  row: "is-center",
  roadmap: "is-boxed is-center is-roadmap",
};

const tier = computed(() => TIER_OF[props.variant]);
const iconName = computed(() => {
  if (props.icon === "none") return undefined;
  if (props.icon) return props.icon;
  return tier.value === "inline" || !props.title ? undefined : "info";
});

/** DEV: 한 화면에 같은 제목의 빈 상태가 둘 이상 */
function warnDuplicateTitle(): void {
  if (!props.title || warnedTitles.has(props.title)) return;
  const same = [...document.querySelectorAll(".ui-empty-title")].filter(
    (el) => el.textContent?.trim() === props.title,
  );
  if (same.length < 2) return;
  warnedTitles.add(props.title);
  console.warn(
    `[EmptyState] 같은 제목의 빈 상태가 한 화면에 ${String(same.length)}번 — 같은 이유는 한 곳만 그리세요(왼쪽 목록이 비면 오른쪽 상세는 v-if 로 숨김). (title: ${props.title})`,
  );
}

if (import.meta.env.DEV) {
  onMounted(warnDuplicateTitle);
  onUpdated(warnDuplicateTitle);
}
</script>

<template>
  <tr v-if="variant === 'row'" class="ui-empty-row">
    <td :colspan="colspan">
      <EmptyState
        :title="title"
        :description="description"
        variant="center"
        data-variant="inline"
        :icon="icon ?? 'none'"
        :role="role"
      >
        <template v-if="$slots.default" #default><slot /></template>
        <template v-if="$slots.actions" #actions>
          <slot name="actions" />
        </template>
      </EmptyState>
    </td>
  </tr>
  <div
    v-else
    class="ui-empty"
    :class="[VARIANT_CLASSES[variant], { 'is-page': variant === 'page' }]"
    :data-variant="tier"
    :data-icon="iconName"
    :role="role"
  >
    <b v-if="title" class="ui-empty-title">
      <UiIcon v-if="iconName" :name="iconName" :size="16" />{{ title }}
    </b>
    <p v-if="description">{{ description }}</p>
    <ol v-if="steps.length" class="ui-empty-steps" aria-label="진행 단계">
      <li v-for="(step, index) in steps" :key="`${index}-${step}`">
        <span class="ui-empty-step-no" aria-hidden="true">{{ index + 1 }}</span>
        {{ step }}
      </li>
    </ol>
    <slot />
    <div v-if="$slots.actions" class="actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
/* 82차 sys-C8(UXS82-10): 빈 상태 설명 글 줄 길이 상한(`--ui-measure` 72ch). 가운데 정렬 변형은 상자를 가운데에 둔다. */
.ui-empty > p {
  max-width: min(100%, var(--ui-measure, 72ch));
}

.ui-empty.is-center > p {
  margin-inline: auto;
}

/* roadmap 단계 사슬 — 번호 칩 + 글, 사이는 가는 화살표(CSS). 폭이 모자라면 줄바꿈하고 마지막 칩 뒤 화살표는 없다. */
.ui-empty-steps {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--ui-gap-xs) var(--ui-gap-sm);
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}

.ui-empty-steps > li {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-gap-xs);
  color: var(--ui-text-sub);
  font-size: var(--ui-fs-sm);
  font-weight: 700;
  white-space: nowrap;
}

.ui-empty-steps > li:not(:last-child)::after {
  margin-left: var(--ui-gap-xs);
  color: var(--ui-text-3);
  content: "→" / "";
}

.ui-empty-step-no {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-neutral-bg);
  color: var(--ui-text-2);
  font-size: var(--ui-fs-xs);
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}
</style>
